// ─────────────────────────────────────────────────────────────────────────────
// Single model registry — the ONE place any Gemini model identifier, per-agent
// fallback, thinking level, or service tier is declared.
//
// Rationale: before REG-1, ~60 raw model string literals were scattered across
// agentPipeline.ts, keeperService.ts, featureFlows.ts, capitolGeminiService.ts,
// spinoffEnricher.ts, analysis.ts, and server.ts. Consolidating them here makes
// the model lineup auditable and lets a model migration change the lineup in one
// place.
//
// IMPORTANT: this file is deliberately the sole location where any raw Gemini
// model identifier ("gemini-3.8-flash" / "gemini-3.6-flash" /
// "gemini-3.5-flash-lite", and any older grandfathered string) appears.
// Everything else references MODELS.* — do not reintroduce a raw model literal
// elsewhere.
//
// MODEL-1 (2026-11-02, ratified by Trey): migrated off the retired
// gemini-3.5-flash alias (which Google was auto-routing to 3.6-flash) and the
// 3.1-flash-lite line (shuts down 2027-05-07):
//   • Oracle (C): 3.8-flash high, 3.6-flash fallback.
//   • In-pipeline mid agents (B/D/E) + Oracle sub-call + Funnel-edge: 3.6-flash.
//   • Lite/background roles (A/F/Funnel/Capitol/Spinoff/PI): 3.5-flash-lite minimal.
// serviceTier 'priority' retained ONLY for the latency-critical in-pipeline
// agents (C, B, D, E) and the Oracle sub-call; lite/background roles run on the
// default (standard) tier. buildGeminiConfig applies entry.serviceTier per call.
// ─────────────────────────────────────────────────────────────────────────────

export type ThinkingLevel = 'minimal' | 'low' | 'medium' | 'high';

export interface ModelEntry {
  /** Primary model for this agent. */
  primary: string;
  /** Fallback model used when the primary 503s / 403s / is unavailable. */
  fallback: string;
  /** Thinking level in effect for this agent's primary call. */
  thinkingLevel: ThinkingLevel;
  /** Priority service tier, where the agent runs on it (else default/standard tier). */
  serviceTier?: 'priority';
}

export type AgentKey =
  | 'ORACLE'
  | 'ORACLE_SUB'
  | 'MATHEMATICIAN'
  | 'ANALYST'
  | 'KEEPER'
  | 'HISTORIAN'
  | 'PULSE'
  | 'FUNNEL'
  | 'FUNNEL_EDGE'
  | 'CAPITOL'
  | 'SPINOFF_ENRICH'
  | 'PI_SHORTLIST'
  | 'HEALTHCHECK';

// Retained for backward compatibility with any importer; tier is now applied
// per-entry via ModelEntry.serviceTier (see buildGeminiConfig), not universally.
export const PRIORITY_TIER = 'priority' as const;

export const MODELS: Readonly<Record<AgentKey, ModelEntry>> = Object.freeze({
  // Agent C — synthesis. 3.8-flash @ thinking high; 503/403 fallback to 3.6-flash.
  ORACLE: {
    primary: 'gemini-3.8-flash',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'high',
    serviceTier: 'priority',
  },
  // Agent C sub-call fallback (the in-synthesis "stable Flash" retry site).
  ORACLE_SUB: {
    primary: 'gemini-3.6-flash',
    fallback: 'gemini-3.5-flash-lite',
    thinkingLevel: 'low',
    serviceTier: 'priority',
  },
  // Agent B — DCF assumption set.
  MATHEMATICIAN: {
    primary: 'gemini-3.6-flash',
    fallback: 'gemini-3.5-flash-lite',
    thinkingLevel: 'low',
    serviceTier: 'priority',
  },
  // Agent D — live web + EDGAR intelligence (Google Search grounded).
  ANALYST: {
    primary: 'gemini-3.6-flash',
    fallback: 'gemini-3.5-flash-lite',
    thinkingLevel: 'low',
    serviceTier: 'priority',
  },
  // Agent E — portfolio position advisor (Keeper).
  KEEPER: {
    primary: 'gemini-3.6-flash',
    fallback: 'gemini-3.5-flash-lite',
    thinkingLevel: 'low',
    serviceTier: 'priority',
  },
  // Agent A — historical fundamentals audit. Fallback is now a real different model.
  HISTORIAN: {
    primary: 'gemini-3.5-flash-lite',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'minimal',
  },
  // Agent F — brand pulse (Google Search grounded).
  PULSE: {
    primary: 'gemini-3.5-flash-lite',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'minimal',
  },
  // Funnel screener — Stage 3 structural-impairment classification pass.
  FUNNEL: {
    primary: 'gemini-3.5-flash-lite',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'minimal',
  },
  // Pre-Inflection "edge" detector — 3.6-flash @ thinking medium, search grounded.
  FUNNEL_EDGE: {
    primary: 'gemini-3.6-flash',
    fallback: 'gemini-3.5-flash-lite',
    thinkingLevel: 'medium',
  },
  // Capitol Edge legislation analyzer.
  CAPITOL: {
    primary: 'gemini-3.5-flash-lite',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'minimal',
  },
  // Spin-off enricher (parent/child/standalone resolution).
  SPINOFF_ENRICH: {
    primary: 'gemini-3.5-flash-lite',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'minimal',
  },
  // Pre-Inflection Stage-3 shortlister.
  PI_SHORTLIST: {
    primary: 'gemini-3.5-flash-lite',
    fallback: 'gemini-3.6-flash',
    thinkingLevel: 'minimal',
  },
  // Server diagnostic / API-key health probe.
  HEALTHCHECK: {
    primary: 'gemini-3.8-flash',
    fallback: 'gemini-3.5-flash-lite',
    thinkingLevel: 'low',
  },
});

// Load-time guard: gemini-3.8-flash does NOT support the 'minimal' thinking level
// (3.5-flash-lite and 3.6-flash do). Fail fast on a mis-paired registry entry
// rather than at request time against the Gemini API.
for (const [key, entry] of Object.entries(MODELS)) {
  if (entry.primary.startsWith('gemini-3.8') && entry.thinkingLevel === 'minimal') {
    throw new Error(
      `[models] ${key}: ${entry.primary} does not support thinkingLevel 'minimal'. Use 'low' or higher.`,
    );
  }
}
